import { useState, useRef, useEffect, memo } from 'react';
import type { BroadcastGroupSummary } from '@/types/broadcast';
import { Badge } from '@/components/ui/badge';
import { getStatusColor, getStatusLabel, getBroadcastTypeInfo, formatWallClockDate } from '../utils/broadcastUtils';
import { Calendar, MoreVertical, Tag, Copy, Languages, Ban, Edit, Lock } from 'lucide-react';
import { checkBroadcastEditable, SCHEDULED_EDIT_CUTOFF_MIN } from '../utils/broadcastUtils';

interface BroadcastGroupGridProps {
    groups: BroadcastGroupSummary[];
    onView?: (group: BroadcastGroupSummary) => void; // TODO: rota desabilitada temporariamente
    onEdit?: (group: BroadcastGroupSummary) => void;
    onDuplicate: (group: BroadcastGroupSummary) => void;
    onCancel: (group: BroadcastGroupSummary) => void;
}

const CANCELLABLE_STATUSES = new Set(['pending', 'processing']);

function CircleProgress({ percent, status }: { percent: number; status: string }) {
    const r = 18;
    const circ = 2 * Math.PI * r;
    const offset = circ - (percent / 100) * circ;

    const strokeColor =
        status === 'processing' ? '#06b6d4' :
            status === 'sent' ? '#10b981' :
                status === 'partial' ? '#f59e0b' :
                    status === 'failed' ? '#ef4444' :
                        '#f59e0b';

    return (
        <svg width="44" height="44" viewBox="0 0 44 44">
            <circle cx="22" cy="22" r={r} fill="none" stroke="#e5e7eb" strokeWidth="3.5" />
            <circle
                cx="22" cy="22" r={r} fill="none"
                stroke={strokeColor} strokeWidth="3.5"
                strokeDasharray={circ}
                strokeDashoffset={offset}
                strokeLinecap="round"
                transform="rotate(-90 22 22)"
            />
            <text x="22" y="26" textAnchor="middle" fontSize="9" fontWeight="600" fill="#374151">
                {Math.round(percent)}%
            </text>
        </svg>
    );
}

function RowMenu({ onEdit, onDuplicate, onCancel, canEdit, canCancel, editLockReason }: {
    onView?: () => void;
    onEdit?: () => void;
    onDuplicate: () => void;
    onCancel: () => void;
    canEdit: boolean;
    canCancel: boolean;
    editLockReason?: string | null;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div ref={ref} className="relative">
            <button
                onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
                <MoreVertical className="w-4 h-4" />
            </button>
            {open && (
                <div className="absolute right-0 top-8 z-20 bg-white border border-gray-100 rounded-xl shadow-lg py-1 min-w-[200px]">
                    {/* Editar — só quando dentro da janela editavel */}
                    {onEdit && canEdit && (
                        <button
                            onClick={e => { e.stopPropagation(); setOpen(false); onEdit(); }}
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-blue-700 hover:bg-blue-50 transition-colors"
                        >
                            <Edit className="w-4 h-4 text-blue-500" />
                            Editar
                        </button>
                    )}
                    {/* Edição bloqueada — mostra com razão */}
                    {onEdit && !canEdit && editLockReason && (
                        <div
                            className="flex items-start gap-2 px-3 py-2 text-xs text-gray-400 cursor-not-allowed"
                            title={editLockReason}
                        >
                            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                            <div>
                                <p className="font-medium">Edição bloqueada</p>
                                <p className="text-[10px] leading-tight">{editLockReason}</p>
                            </div>
                        </div>
                    )}
                    <button
                        onClick={e => { e.stopPropagation(); setOpen(false); onDuplicate(); }}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        <Copy className="w-4 h-4 text-gray-400" />
                        Duplicar
                    </button>
                    {canCancel && (
                        <button
                            onClick={e => { e.stopPropagation(); setOpen(false); onCancel(); }}
                            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                            <Ban className="w-4 h-4 text-red-400" />
                            Cancelar grupo
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

// `scheduled_at` é wall clock (tz da página) — usa helper sem conversão de TZ
// pra não mostrar 20:51 quando user digitou 23:51.
const formatDate = (dateString?: string) => formatWallClockDate(dateString) || null;

interface GroupRowProps {
    group: BroadcastGroupSummary;
    onView?: (group: BroadcastGroupSummary) => void; // TODO: rota desabilitada temporariamente
    onEdit?: (group: BroadcastGroupSummary) => void;
    onDuplicate: (group: BroadcastGroupSummary) => void;
    onCancel: (group: BroadcastGroupSummary) => void;
}

const GroupRow = memo(function GroupRow({ group, onView, onEdit, onDuplicate, onCancel }: GroupRowProps) {
    const status = group.status;
    const progressPercent = group.success_rate ?? 0;
    const scheduledAt = group.scheduled_at;
    const pagesCount = group.total_pages;
    const totalProfilesSent = group.total_sent;
    const creatorName = group.user_name || null;
    const displayName = group.title || group.name || `Grupo #${group.group_broadcast_id}`;

    // Checa editabilidade — leva em conta status e cutoff de 5min pro envio
    const editCheck = checkBroadcastEditable({
        status,
        is_scheduled: !!scheduledAt,
        scheduled_at: scheduledAt,
    });

    // Métricas adicionais — derivadas do response (zero custo na request).
    const avgPerPage = pagesCount > 0 ? Math.round(totalProfilesSent / pagesCount) : 0;
    const durationSeconds = (() => {
        if (!group.started_at || !group.completed_at) return null;
        const start = new Date(group.started_at).getTime();
        const end = new Date(group.completed_at).getTime();
        const sec = Math.max(0, Math.round((end - start) / 1000));
        return Number.isFinite(sec) ? sec : null;
    })();
    const formatDuration = (s: number | null) => {
        if (s === null) return null;
        if (s < 60) return `${s}s`;
        const m = Math.floor(s / 60);
        const r = s % 60;
        return r > 0 ? `${m}m ${r}s` : `${m}m`;
    };

    // Tipo de envio derivado de message_tag (Standard / Utility / tag custom)
    const typeInfo = getBroadcastTypeInfo(group.message_tag);
    const tags = typeInfo.kind === 'standard' ? [] : [typeInfo.label];
    const aiLanguages = group.ai_translate && group.target_language ? [group.target_language] : [];

    return (
        <div
            className="group/row relative grid grid-cols-[1fr_140px_200px_40px] px-6 py-4 items-center transition-all duration-150 hover:bg-gradient-to-r hover:from-slate-50 hover:to-transparent cursor-pointer"
            onClick={() => onView?.(group)}
        >
            {/* left accent on hover */}
            <span className="absolute left-0 top-0 h-full w-[3px] rounded-r bg-blue-400 opacity-0 group-hover/row:opacity-100 transition-opacity duration-150" />

            {/* Fluxo */}
            <div className="pr-6 min-w-0">
                <div className="mb-1">
                    <Badge className={`${getStatusColor(status, !!scheduledAt)} border rounded-full text-[11px] px-2 py-0 font-medium`}>
                        {getStatusLabel(status, !!scheduledAt)}
                    </Badge>
                </div>
                <p
                    className="text-[15px] font-bold text-gray-900 truncate mb-1.5 transition-colors group-hover/row:text-gray-700"
                    title="Clique para duplicar"
                    onClick={e => { e.stopPropagation(); onDuplicate(group); }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#2563eb')}
                    onMouseLeave={e => (e.currentTarget.style.color = '')}
                    style={{ cursor: 'pointer' }}
                >
                    {displayName}
                </p>
                <div className="flex items-center gap-1.5 flex-wrap">
                    {tags.map((tag, i) => (
                        <span key={`tag-${i}`} className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600 bg-amber-50 border border-amber-100/50 rounded-md px-2 py-0.5">
                            <Tag className="w-3 h-3 text-amber-400" strokeWidth={1.5} />
                            {tag}
                        </span>
                    ))}
                    {aiLanguages.map((lang, i) => (
                        <span key={`ai-lang-${i}`} className="inline-flex items-center gap-1 text-[11px] font-medium text-cyan-600 bg-cyan-50 border border-cyan-100/50 rounded-md px-2 py-0.5">
                            <Languages className="w-3 h-3 text-cyan-400" strokeWidth={1.5} />
                            AI: {lang}
                        </span>
                    ))}
                    <span className="inline-flex items-center text-[11px] font-medium text-blue-600 bg-blue-50 border border-blue-100/50 rounded-md px-2 py-0.5">
                        {pagesCount} {pagesCount === 1 ? 'página' : 'páginas'}
                    </span>
                </div>
            </div>

            {/* Progresso */}
            <div className="flex items-center gap-3">
                <CircleProgress percent={progressPercent} status={status} />
                <div>
                    <p className="text-sm font-semibold text-gray-800">
                        {totalProfilesSent.toLocaleString('pt-BR')}
                    </p>
                    <p className="text-[11px] text-gray-400">
                        {Math.round(group.success_rate ?? 0)}% sucesso
                        {pagesCount > 0 && (
                            <> · {avgPerPage.toLocaleString('pt-BR')}/pág</>
                        )}
                        {formatDuration(durationSeconds) && (
                            <> · {formatDuration(durationSeconds)}</>
                        )}
                    </p>
                </div>
            </div>

            {/* Agendamento */}
            <div className="text-sm">
                {scheduledAt ? (
                    <div className="flex items-center gap-1.5 text-gray-700 mb-0.5">
                        <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" strokeWidth={1.5} />
                        <span className="font-medium">{formatDate(scheduledAt)}</span>
                    </div>
                ) : (
                    <span className="text-gray-400 text-xs">Sem agendamento</span>
                )}
                {creatorName && (
                    <p className="text-[12px] text-gray-400 truncate max-w-[180px]">
                        Por {creatorName}
                    </p>
                )}
            </div>

            {/* Menu */}
            <div className="flex justify-end" onClick={e => e.stopPropagation()}>
                <RowMenu
                    onView={() => { /* TODO: rota desabilitada temporariamente */ }}
                    onEdit={onEdit ? () => onEdit(group) : undefined}
                    onDuplicate={() => onDuplicate(group)}
                    onCancel={() => onCancel(group)}
                    canEdit={editCheck.editable}
                    canCancel={CANCELLABLE_STATUSES.has(status)}
                    editLockReason={editCheck.editable ? null : editCheck.reason}
                />
            </div>
        </div>
    );
});

export default function BroadcastGroupGrid({ groups, onView, onEdit, onDuplicate, onCancel }: BroadcastGroupGridProps) {
    // Mostra alerta sobre janela de edição se houver agendados pendentes
    const hasScheduledPending = groups.some(g =>
        g.status === 'pending' && g.scheduled_at
    );

    return (
        <div className="space-y-3">
            {hasScheduledPending && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-xl px-4 py-2.5 flex items-start gap-2.5">
                    <Calendar className="w-4 h-4 text-cyan-600 mt-0.5 shrink-0" />
                    <p className="text-xs text-cyan-800 leading-relaxed">
                        Você pode <strong>editar</strong> broadcasts agendados até{' '}
                        <strong>{SCHEDULED_EDIT_CUTOFF_MIN} minutos antes</strong> do envio.
                        Depois desse prazo, a edição fica bloqueada (você ainda pode cancelar).
                    </p>
                </div>
            )}
            <div className="bg-white rounded-2xl border border-gray-100">
                {/* Header */}
                <div className="grid grid-cols-[1fr_140px_200px_40px] px-6 py-3 border-b border-gray-100 bg-gray-50/60">
                    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Fluxo</span>
                    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Progresso</span>
                    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Agendamento</span>
                    <span />
                </div>

                {/* Rows */}
                <div className="divide-y divide-gray-100">
                    {groups.map((group) => (
                        <GroupRow
                            key={group.group_broadcast_id}
                            group={group}
                            onView={onView}
                            onEdit={onEdit}
                            onDuplicate={onDuplicate}
                            onCancel={onCancel}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
